import React, { useMemo } from "react";
import { useTrades, useCapital } from "../useJournal";

const fmt = (n,pre="₹") => { if(n==null||isNaN(n))return"—"; return pre+Number(n).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2}); };

function StatCard({label,value,sub,color}) {
  return (
    <div style={{background:"white",border:"1px solid #e5e7eb",borderRadius:8,padding:"16px 20px"}}>
      <div style={{fontSize:11,color:"#6b7280",fontWeight:600,textTransform:"uppercase",marginBottom:6}}>{label}</div>
      <div style={{fontSize:22,fontWeight:800,color:color||"#111827",fontFamily:"monospace"}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:"#9ca3af",marginTop:4}}>{sub}</div>}
    </div>
  );
}

export default function SummaryPage() {
  const { trades } = useTrades();
  const { totalCapital } = useCapital();

  const stats = useMemo(() => {
    const closed = trades.filter(t=>t.status==="closed");
    if (!closed.length) return null;
    const allExits = closed.flatMap(t=>(t.exits||[]).map(e=>({...e,actual_risk:t.actual_risk})));
    const winners = allExits.filter(e=>e.pnl>0);
    const losers = allExits.filter(e=>e.pnl<=0);
    const totalPnL = allExits.reduce((s,e)=>s+e.pnl,0);
    const avgWin = winners.length?winners.reduce((s,e)=>s+e.pnl,0)/winners.length:0;
    const avgLoss = losers.length?Math.abs(losers.reduce((s,e)=>s+e.pnl,0)/losers.length):0;
    const winRate = allExits.length?winners.length/allExits.length:0;
    const grossWin = winners.reduce((s,e)=>s+e.pnl,0);
    const grossLoss = Math.abs(losers.reduce((s,e)=>s+e.pnl,0));
    const profitFactor = grossLoss>0?grossWin/grossLoss:grossWin>0?Infinity:0;
    const avgWinR = winners.length&&allExits[0]?.actual_risk?winners.reduce((s,e)=>s+(e.pnl/(e.actual_risk||1)),0)/winners.length:0;
    const avgLossR = losers.length&&allExits[0]?.actual_risk?Math.abs(losers.reduce((s,e)=>s+(e.pnl/(e.actual_risk||1)),0)/losers.length):0;
    const expectancyR = (winRate*avgWinR)-((1-winRate)*avgLossR);
    const expectancyCurrency = (winRate*avgWin)-((1-winRate)*avgLoss);
    const rDist = {loss:0,r1:0,r2:0,r3:0,r4:0,r5plus:0};
    allExits.forEach(e=>{ const r=e.actual_risk>0?e.pnl/e.actual_risk:0; if(r<0)rDist.loss++;else if(r<1)rDist.r1++;else if(r<2)rDist.r2++;else if(r<3)rDist.r3++;else if(r<4)rDist.r4++;else rDist.r5plus++; });
    return { totalTrades:closed.length,totalExits:allExits.length,winners:winners.length,losers:losers.length,winRate,totalPnL,avgWin,avgLoss,profitFactor,expectancyR,expectancyCurrency,roi:totalCapital>0?totalPnL/totalCapital:0,rDist };
  }, [trades,totalCapital]);

  if (!stats) return (
    <div style={{textAlign:"center",padding:60,color:"#9ca3af"}}>
      <div style={{fontSize:32,marginBottom:12}}>📊</div>
      <div style={{fontWeight:600}}>No closed trades yet</div>
      <div style={{fontSize:13,marginTop:6}}>Complete some trades to see your performance summary</div>
    </div>
  );

  const maxR = Math.max(...Object.values(stats.rDist),1);

  return (
    <div style={{maxWidth:1000,margin:"0 auto"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:20}}>
        <StatCard label="Win Rate" value={`${(stats.winRate*100).toFixed(1)}%`} sub={`${stats.winners}W / ${stats.losers}L`} color={stats.winRate>=0.4?"#16a34a":"#dc2626"} />
        <StatCard label="Total P&L" value={fmt(stats.totalPnL)} color={stats.totalPnL>=0?"#16a34a":"#dc2626"} />
        <StatCard label="Profit Factor" value={stats.profitFactor===Infinity?"∞":stats.profitFactor.toFixed(2)} sub="Gross Win / Gross Loss" color={stats.profitFactor>=1.5?"#16a34a":"#dc2626"} />
        <StatCard label="ROI" value={`${(stats.roi*100).toFixed(2)}%`} color={stats.roi>=0?"#16a34a":"#dc2626"} />
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:16,marginBottom:20}}>
        <StatCard label="Expectancy (R)" value={`${stats.expectancyR.toFixed(2)}R`} color={stats.expectancyR>=0.3?"#16a34a":"#dc2626"} />
        <StatCard label="Expectancy (₹)" value={fmt(stats.expectancyCurrency)} />
        <StatCard label="Avg Win" value={fmt(stats.avgWin)} color="#16a34a" />
        <StatCard label="Avg Loss" value={fmt(stats.avgLoss)} color="#dc2626" />
      </div>

      <div style={{background:"white",border:"1px solid #e5e7eb",borderRadius:8,padding:20,marginBottom:20}}>
        <div style={{fontWeight:700,marginBottom:16}}>R-Distribution</div>
        <div style={{display:"flex",gap:16,alignItems:"flex-end",height:120}}>
          {[{label:"Loss",value:stats.rDist.loss,color:"#dc2626"},{label:"<1R",value:stats.rDist.r1,color:"#f97316"},{label:"1-2R",value:stats.rDist.r2,color:"#eab308"},{label:"2-3R",value:stats.rDist.r3,color:"#84cc16"},{label:"3-4R",value:stats.rDist.r4,color:"#22c55e"},{label:"5R+",value:stats.rDist.r5plus,color:"#16a34a"}].map(b=>(
            <div key={b.label} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{fontSize:11,fontWeight:700}}>{b.value}</div>
              <div style={{width:"100%",background:b.color,borderRadius:"4px 4px 0 0",height:maxR>0?`${Math.max(4,(b.value/maxR)*90)}px`:"4px"}} />
              <div style={{fontSize:10,color:"#6b7280"}}>{b.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{background:"white",border:"1px solid #e5e7eb",borderRadius:8,padding:20}}>
        <div style={{fontWeight:700,marginBottom:12}}>Closed Trades</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead><tr style={{borderBottom:"1px solid #e5e7eb"}}>{["Symbol","Entry","Exit Avg","Qty","P&L","Tag"].map(h=><th key={h} style={{padding:"6px 8px",textAlign:"left",fontSize:11,color:"#6b7280",fontWeight:600}}>{h}</th>)}</tr></thead>
          <tbody>
            {trades.filter(t=>t.status==="closed").map(trade=>{
              const exits=trade.exits||[];
              const totalQty=exits.reduce((s,e)=>s+e.exit_quantity,0);
              const avgExit=totalQty>0?exits.reduce((s,e)=>s+e.exit_price*e.exit_quantity,0)/totalQty:0;
              const totalPnL=exits.reduce((s,e)=>s+e.pnl,0);
              return (
                <tr key={trade.id} style={{borderBottom:"1px solid #f3f4f6"}}>
                  <td style={{padding:"8px",fontWeight:700}}>{trade.symbol}</td>
                  <td style={{padding:"8px",fontFamily:"monospace"}}>{fmt(trade.entry_price)}</td>
                  <td style={{padding:"8px",fontFamily:"monospace"}}>{fmt(avgExit)}</td>
                  <td style={{padding:"8px"}}>{totalQty}</td>
                  <td style={{padding:"8px",fontFamily:"monospace",fontWeight:700,color:totalPnL>=0?"#16a34a":"#dc2626"}}>{fmt(totalPnL)}</td>
                  <td style={{padding:"8px",color:"#6b7280"}}>{trade.tag}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
